import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { 
  Users,
  UserPlus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Key,
  Shield,
  ShieldOff,
  Download,
  Loader2,
  X,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Truck
} from 'lucide-react';
import { toast } from 'sonner';
import { getUserErrorMessage } from '../../utils/errorMessages';

const AdminUsers = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [makeSupplierModalOpen, setMakeSupplierModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [supplierCompanyName, setSupplierCompanyName] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'user',
    job_title: '',
    organization_id: ''
  });
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchData();
  }, [roleFilter, statusFilter]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const [usersRes, statsRes, orgsRes] = await Promise.all([
        api.get(`/admin/users?${params.toString()}`),
        api.get('/admin/users/stats'),
        api.get('/admin/organizations')
      ]);
      
      setUsers(usersRes.data);
      setStats(statsRes.data);
      setOrganizations(orgsRes.data);
    } catch (error) {
      toast.error('Gebruikers laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!formData.email || !formData.password || !formData.full_name) {
      toast.error('Vul alle verplichte velden in');
      return;
    }

    setActionLoading(true);
    try {
      await api.post('/admin/users', formData);
      toast.success('Gebruiker aangemaakt');
      setCreateModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Aanmaken mislukt'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    setActionLoading(true);
    try {
      const updateData = {
        full_name: formData.full_name,
        email: formData.email,
        job_title: formData.job_title || null,
        organization_id: formData.organization_id || null
      };
      
      await api.patch(`/admin/users/${currentUser.id}`, updateData);
      toast.success('Gebruiker bijgewerkt');
      setEditModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Bijwerken mislukt'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await api.patch(`/admin/users/${userId}/role`, { role: newRole });
      toast.success('Rol gewijzigd');
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Rol wijzigen mislukt'));
    }
  };

  const handleToggleStatus = async (userId, isActive) => {
    try {
      await api.patch(`/admin/users/${userId}/status`, { is_active: !isActive });
      toast.success(isActive ? 'Gebruiker geblokkeerd' : 'Gebruiker geactiveerd');
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Status wijzigen mislukt'));
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Wachtwoord moet minimaal 6 tekens zijn');
      return;
    }

    setActionLoading(true);
    try {
      await api.post(`/admin/users/${currentUser.id}/reset-password`, { new_password: newPassword });
      toast.success('Wachtwoord gereset');
      setResetPasswordModalOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Reset mislukt'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    setActionLoading(true);
    try {
      await api.delete(`/admin/users/${currentUser.id}`);
      toast.success('Gebruiker verwijderd');
      setDeleteModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Verwijderen mislukt'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    setActionLoading(true);
    try {
      const response = await api.post('/admin/users/bulk-delete', { user_ids: selectedUsers });
      toast.success(`${response.data.deleted_count} gebruiker(s) verwijderd`);
      setBulkDeleteModalOpen(false);
      setSelectedUsers([]);
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Bulk verwijderen mislukt'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/admin/users/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export gedownload');
    } catch (error) {
      toast.error('Export mislukt');
    }
  };

  const handleMakeSupplier = (user) => {
    setCurrentUser(user);
    setSupplierCompanyName(user.full_name || '');
    setMakeSupplierModalOpen(true);
  };

  const confirmMakeSupplier = async () => {
    if (!currentUser) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${currentUser.id}/make-supplier`, {
        company_name: supplierCompanyName || null
      });
      toast.success(`${currentUser.email} is nu leverancier`);
      setMakeSupplierModalOpen(false);
      setCurrentUser(null);
      setSupplierCompanyName('');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Actie mislukt'));
    } finally {
      setActionLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'user',
      job_title: '',
      organization_id: ''
    });
  };

  const openEditModal = (user) => {
    setCurrentUser(user);
    setFormData({
      email: user.email,
      password: '',
      full_name: user.full_name,
      role: user.role,
      job_title: user.job_title || '',
      organization_id: user.organization_id || ''
    });
    setEditModalOpen(true);
  };

  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  const getRoleBadge = (role) => {
    const variants = {
      admin: 'border-transparent bg-purple-50 text-purple-700',
      partner: 'border-transparent bg-blue-50 text-blue-700',
      user: 'border-transparent bg-neutral-100 text-neutral-700'
    };
    const labels = {
      admin: 'Admin',
      partner: 'Leverancier',
      user: 'Klant'
    };
    return (
      <Badge className={variants[role] || variants.user}>
        {labels[role] || 'Klant'}
      </Badge>
    );
  };

  const getStatusBadge = (isActive) => {
    return isActive !== false ? (
      <Badge className="border-transparent bg-green-50 text-green-700">
        <CheckCircle className="h-3 w-3 mr-1" strokeWidth={1.5} />
        Actief
      </Badge>
    ) : (
      <Badge className="border-transparent bg-red-50 text-red-700">
        <XCircle className="h-3 w-3 mr-1" strokeWidth={1.5} />
        Geblokkeerd
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Apply search filter
  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">
              Admin
            </p>
            <h1 className="text-2xl font-bold tracking-tight text-black">
              Gebruikersbeheer
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="border-neutral-200"
              onClick={handleExport}
              data-testid="export-users-button"
            >
              <Download className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Exporteren
            </Button>
            <Button
              size="sm"
              className="bg-black text-white hover:bg-neutral-800"
              onClick={() => {
                resetForm();
                setCreateModalOpen(true);
              }}
              data-testid="create-user-button"
            >
              <UserPlus className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Nieuwe Gebruiker
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-neutral-100 flex items-center justify-center">
                    <Users className="h-5 w-5 text-neutral-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-black">{stats.total}</p>
                    <p className="text-xs text-neutral-500">Totaal</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{stats.active}</p>
                    <p className="text-xs text-neutral-500">Actief</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-neutral-100 flex items-center justify-center">
                    <Users className="h-5 w-5 text-neutral-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-black">{stats.by_role?.customers || 0}</p>
                    <p className="text-xs text-neutral-500">Klanten</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Users className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{stats.by_role?.partners || 0}</p>
                    <p className="text-xs text-neutral-500">Leveranciers</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-purple-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{stats.by_role?.admins || 0}</p>
                    <p className="text-xs text-neutral-500">Admins</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters and Search */}
        <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                <Input
                  type="text"
                  placeholder="Zoek op naam of email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 border-neutral-200"
                  data-testid="user-search-input"
                />
              </div>

              {/* Role Filter */}
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-40 border-neutral-200" data-testid="role-filter">
                  <SelectValue placeholder="Rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle rollen</SelectItem>
                  <SelectItem value="user">Klanten</SelectItem>
                  <SelectItem value="partner">Leveranciers</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 border-neutral-200" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle statussen</SelectItem>
                  <SelectItem value="active">Actief</SelectItem>
                  <SelectItem value="inactive">Geblokkeerd</SelectItem>
                </SelectContent>
              </Select>

              {/* Bulk Actions */}
              {selectedUsers.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleteModalOpen(true)}
                  data-testid="bulk-delete-button"
                >
                  <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Verwijder ({selectedUsers.length})
                </Button>
              )}
            </div>

            {/* Results count */}
            <p className="text-xs text-neutral-500 mt-3">
              {filteredUsers.length} van {users.length} gebruikers
            </p>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
          <CardContent className="p-0">
            <div className="overflow-x-auto max-h-[600px]">
              <table className="w-full">
                <thead className="sticky top-0 z-10 bg-white dark:bg-neutral-900">
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    <th className="p-4 text-left bg-neutral-50 dark:bg-neutral-800">
                      <Checkbox
                        checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="p-4 text-left text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 bg-neutral-50 dark:bg-neutral-800">
                      Gebruiker
                    </th>
                    <th className="p-4 text-left text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 bg-neutral-50 dark:bg-neutral-800">
                      Rol
                    </th>
                    <th className="p-4 text-left text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 bg-neutral-50 dark:bg-neutral-800">
                      Organisatie
                    </th>
                    <th className="p-4 text-left text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 bg-neutral-50 dark:bg-neutral-800">
                      Status
                    </th>
                    <th className="p-4 text-left text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 bg-neutral-50 dark:bg-neutral-800">
                      Laatste login
                    </th>
                    <th className="p-4 text-right text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 bg-neutral-50 dark:bg-neutral-800">
                      Acties
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, index) => (
                    <tr 
                      key={user.id} 
                      className={`border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors ${
                        index % 2 === 1 ? 'bg-neutral-50/50 dark:bg-neutral-800/20' : ''
                      }`}
                      data-testid={`user-row-${user.id}`}
                    >
                      <td className="p-4">
                        <Checkbox
                          checked={selectedUsers.includes(user.id)}
                          onCheckedChange={() => toggleSelectUser(user.id)}
                        />
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center">
                            <span className="text-sm font-semibold text-neutral-600 dark:text-neutral-300">
                              {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-black dark:text-white">{user.full_name}</p>
                            <p className="text-xs text-neutral-500">{user.email}</p>
                            {user.job_title && (
                              <p className="text-xs text-neutral-400">{user.job_title}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">{getRoleBadge(user.role)}</td>
                      <td className="p-4">
                        <span className="text-sm text-neutral-600 dark:text-neutral-400">
                          {user.organization_name || '-'}
                        </span>
                      </td>
                      <td className="p-4">{getStatusBadge(user.is_active)}</td>
                      <td className="p-4">
                        <span className="text-sm text-neutral-500">
                          {user.last_login ? formatDate(user.last_login) : 'Nooit'}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`user-actions-${user.id}`}>
                              <MoreHorizontal className="h-4 w-4" strokeWidth={1.5} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditModal(user)}>
                              <Edit className="h-4 w-4 mr-2" strokeWidth={1.5} />
                              Bewerken
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setCurrentUser(user);
                              setResetPasswordModalOpen(true);
                            }}>
                              <Key className="h-4 w-4 mr-2" strokeWidth={1.5} />
                              Wachtwoord resetten
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleChangeRole(user.id, user.role === 'admin' ? 'user' : 'admin')}>
                              <Shield className="h-4 w-4 mr-2" strokeWidth={1.5} />
                              {user.role === 'admin' ? 'Admin rechten intrekken' : 'Admin maken'}
                            </DropdownMenuItem>
                            {user.role !== 'admin' && (
                              <DropdownMenuItem onClick={() => handleMakeSupplier(user)}>
                                <Truck className="h-4 w-4 mr-2" strokeWidth={1.5} />
                                Maak leverancier
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleToggleStatus(user.id, user.is_active !== false)}>
                              {user.is_active !== false ? (
                                <>
                                  <ShieldOff className="h-4 w-4 mr-2" strokeWidth={1.5} />
                                  Blokkeren
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-2" strokeWidth={1.5} />
                                  Activeren
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => {
                                setCurrentUser(user);
                                setDeleteModalOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                              Verwijderen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredUsers.length === 0 && (
              <div className="py-12 text-center">
                <Users className="h-12 w-12 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-sm text-neutral-500">Geen gebruikers gevonden</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create User Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nieuwe Gebruiker</DialogTitle>
            <DialogDescription>
              Maak een nieuwe gebruiker aan met de gewenste rol.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="create-email">E-mail *</Label>
              <Input
                id="create-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                placeholder="gebruiker@example.com"
                data-testid="create-email-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-password">Wachtwoord *</Label>
              <Input
                id="create-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(f => ({ ...f, password: e.target.value }))}
                placeholder="Minimaal 6 tekens"
                data-testid="create-password-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-name">Naam *</Label>
              <Input
                id="create-name"
                value={formData.full_name}
                onChange={(e) => setFormData(f => ({ ...f, full_name: e.target.value }))}
                placeholder="Volledige naam"
                data-testid="create-name-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-role">Rol *</Label>
              <Select 
                value={formData.role} 
                onValueChange={(v) => setFormData(f => ({ ...f, role: v }))}
              >
                <SelectTrigger data-testid="create-role-select">
                  <SelectValue placeholder="Selecteer rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Klant</SelectItem>
                  <SelectItem value="partner">Leverancier</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-job-title">Functie</Label>
              <Input
                id="create-job-title"
                value={formData.job_title}
                onChange={(e) => setFormData(f => ({ ...f, job_title: e.target.value }))}
                placeholder="bijv. IT Manager"
                data-testid="create-jobtitle-input"
              />
            </div>
            {formData.role === 'user' && (
              <div className="grid gap-2">
                <Label htmlFor="create-org">Organisatie</Label>
                <Select 
                  value={formData.organization_id} 
                  onValueChange={(v) => setFormData(f => ({ ...f, organization_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="create-org-select">
                    <SelectValue placeholder="Selecteer organisatie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Geen organisatie</SelectItem>
                    {organizations.map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              Annuleren
            </Button>
            <Button 
              onClick={handleCreateUser} 
              disabled={actionLoading}
              className="bg-black text-white hover:bg-neutral-800"
              data-testid="create-user-submit"
            >
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Aanmaken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Gebruiker Bewerken</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-email">E-mail</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                data-testid="edit-email-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Naam</Label>
              <Input
                id="edit-name"
                value={formData.full_name}
                onChange={(e) => setFormData(f => ({ ...f, full_name: e.target.value }))}
                data-testid="edit-name-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-job-title">Functie</Label>
              <Input
                id="edit-job-title"
                value={formData.job_title}
                onChange={(e) => setFormData(f => ({ ...f, job_title: e.target.value }))}
                data-testid="edit-jobtitle-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-org">Organisatie</Label>
              <Select 
                value={formData.organization_id || "none"} 
                onValueChange={(v) => setFormData(f => ({ ...f, organization_id: v === "none" ? "" : v }))}
              >
                <SelectTrigger data-testid="edit-org-select">
                  <SelectValue placeholder="Selecteer organisatie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Geen organisatie</SelectItem>
                  {organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Annuleren
            </Button>
            <Button 
              onClick={handleUpdateUser} 
              disabled={actionLoading}
              className="bg-black text-white hover:bg-neutral-800"
              data-testid="edit-user-submit"
            >
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={resetPasswordModalOpen} onOpenChange={setResetPasswordModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Wachtwoord Resetten</DialogTitle>
            <DialogDescription>
              Stel een nieuw wachtwoord in voor {currentUser?.full_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="new-password">Nieuw Wachtwoord</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimaal 6 tekens"
                data-testid="reset-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordModalOpen(false)}>
              Annuleren
            </Button>
            <Button 
              onClick={handleResetPassword} 
              disabled={actionLoading}
              className="bg-black text-white hover:bg-neutral-800"
              data-testid="reset-password-submit"
            >
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Resetten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" strokeWidth={1.5} />
              Gebruiker Verwijderen
            </DialogTitle>
            <DialogDescription>
              Weet u zeker dat u {currentUser?.full_name} wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Annuleren
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteUser} 
              disabled={actionLoading}
              data-testid="delete-user-confirm"
            >
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Modal */}
      <Dialog open={bulkDeleteModalOpen} onOpenChange={setBulkDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" strokeWidth={1.5} />
              Meerdere Gebruikers Verwijderen
            </DialogTitle>
            <DialogDescription>
              Weet u zeker dat u {selectedUsers.length} gebruiker(s) wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteModalOpen(false)}>
              Annuleren
            </Button>
            <Button 
              variant="destructive"
              onClick={handleBulkDelete} 
              disabled={actionLoading}
              data-testid="bulk-delete-confirm"
            >
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Verwijderen ({selectedUsers.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Make Supplier Modal */}
      <Dialog open={makeSupplierModalOpen} onOpenChange={setMakeSupplierModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-[#635bff]" strokeWidth={1.5} />
              Maak Leverancier
            </DialogTitle>
            <DialogDescription>
              {currentUser?.email} krijgt toegang tot het leverancier portaal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="supplier-company">Bedrijfsnaam voor leverancier</Label>
              <Input
                id="supplier-company"
                value={supplierCompanyName}
                onChange={(e) => setSupplierCompanyName(e.target.value)}
                placeholder="Naam van het leveranciersbedrijf"
                data-testid="supplier-company-input"
              />
              <p className="text-xs text-neutral-500">
                Dit wordt de bedrijfsnaam in het leverancier portaal. De gebruiker kan dit later aanpassen.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMakeSupplierModalOpen(false)}>
              Annuleren
            </Button>
            <Button 
              onClick={confirmMakeSupplier} 
              disabled={actionLoading}
              className="bg-[#635bff] text-white hover:bg-[#4b45c6]"
              data-testid="make-supplier-confirm"
            >
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Maak leverancier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminUsers;
